import { Button, Card, Col, Form, Modal, Row, notification } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import parkService from '../../../services/config/park.service';
import EditTicketTypeModal from './edit_ticket_type_modal';
import AddTicketTypeModal from './add_ticket_type_modal';
import DragDropTicketTypeCard from './drag_drop_ticket_type_card';

// React DND imports
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const TicketType = ({ park_id, ticket_types, refreshPage = () => {} }) => {
    const [add_form] = Form.useForm();
    const [edit_form] = Form.useForm();
    const [state, setState] = useState({
        loading: false,
        edit_mode: false,

        // modal
        edit_modal: null,
        add_new_modal: false,

        // initialize
        all_ticket_types: [],

        // action
        save_loading: false,
        update_loading: false,

        // drag and drop
        items: [],
        draggedItem: null,
        is_updating_sequence: false,
    });

    // Memoize sorted items
    const sortedItems = useMemo(() => {
        if (!ticket_types) return [];
        return [...ticket_types].sort(
            (a, b) => (a?.ParkApplicableTicketType?.sequence || 0) - (b?.ParkApplicableTicketType?.sequence || 0)
        );
    }, [ticket_types]);

    // Update items when ticket_types change
    useEffect(() => {
        setState(state => ({ ...state, items: sortedItems }));
    }, [sortedItems]);

    // Handle hover - only visual reordering, no API call
    const moveCard = useCallback((dragIndex, hoverIndex) => {
        setState(state => {
            const newItems = [...state.items];
            const dragItem = newItems[dragIndex];
            
            // Remove drag item and insert at new position
            newItems.splice(dragIndex, 1);
            newItems.splice(hoverIndex, 0, dragItem);

            return { ...state, items: newItems };
        });
    }, []);

    // Handle drop - save to backend
    const handleDrop = useCallback(async () => {
        if (state.is_updating_sequence) return;

        setState(state => ({ ...state, is_updating_sequence: true }));

        // Prepare sequences for backend
        const sequences = state.items.map((item, index) => ({
            park_applicable_ticket_type_id: item.ParkApplicableTicketType.park_applicable_ticket_type_id,
            sequence: index + 1,
        }));

        try {
            // Call backend to save new sequences
            await parkService.updateParkApplicableTicketTypeSequences(park_id, sequences);
            
            notification.success({
                message: 'Sequence Updated',
                description: 'Ticket type order has been saved successfully.',
                duration: 2,
            });

            // Refresh to get updated data from backend
            refreshPage();
        } catch (error) {
            console.error('Failed to update sequence:', error);
            
            notification.error({
                message: 'Update Failed',
                description: error?.response?.data?.message || 'Failed to update ticket type sequence. Please try again.',
                duration: 3,
            });
            
            // Revert on error - restore original order
            setState(state => ({ ...state, items: sortedItems }));
        } finally {
            setState(state => ({ ...state, is_updating_sequence: false }));
        }
    }, [state.items, state.is_updating_sequence, park_id, refreshPage, sortedItems]);

    const openEditModal = useCallback((park_applicable_ticket_type_id) => {
        setState(state => ({ ...state, edit_modal: park_applicable_ticket_type_id }));
    }, []);

    const saveParkApplicableTicketType = async () => {
        try {
            setState(state => ({ ...state, save_loading: true }));
            const { image, advance_day, change_date_advance_day, date_range, descriptions, min_purchase, max_purchase, min_paying_guest, ticket_type_id } = add_form?.getFieldsValue();

            const saveParkApplicableTicketTypeResponse = await parkService?.saveParkApplicableTicketType(park_id, {
                image: image?.file,
                advance_day,
                change_date_advance_day,
                date_range,
                descriptions,
                min_purchase,
                max_purchase,
                min_paying_guest,
                ticket_type_id,
            });

            const { save_park_applicable_ticket_type } = saveParkApplicableTicketTypeResponse;
            if (save_park_applicable_ticket_type) {
                notification?.success({
                    message: 'Ticket Type Added To Park',
                    description: `${save_park_applicable_ticket_type?.name} has been added successfully.`,
                });

                setState(state => ({ ...state, save_loading: false, add_new_modal: false }));
                add_form.resetFields();
                refreshPage && refreshPage();
            }
        } catch (err) {
            console.error('Save failed:', err);
            notification.error({
                message: 'Save Failed',
                description: err?.response?.data?.message || 'Failed to add ticket type. Please try again.',
            });
            setState(state => ({ ...state, save_loading: false }));
        }
    };

    const updateParkApplicableTicketType = async () => {
        try {
            setState(state => ({ ...state, update_loading: true }));
            const park_applicable_ticket_type_id = state?.edit_modal;
            const { image, advance_day, change_date_advance_day, date_range, descriptions, min_purchase, max_purchase, min_paying_guest } = edit_form?.getFieldsValue();

            const updateParkApplicableTicketTypeResponse = await parkService?.updateParkApplicableTicketType(park_applicable_ticket_type_id, {
                image: image?.file,
                advance_day,
                change_date_advance_day,
                date_range,
                descriptions,
                min_purchase,
                max_purchase,
                min_paying_guest,
            });

            const { update_park_applicable_ticket_type } = updateParkApplicableTicketTypeResponse;
            if (update_park_applicable_ticket_type) {
                notification?.success({
                    message: 'Ticket Type Updated',
                    description: 'Changes have been saved successfully.',
                });

                setState(state => ({ ...state, update_loading: false, edit_modal: false }));
                refreshPage && refreshPage();
            }
        } catch (err) {
            console.error('Update failed:', err);
            notification.error({
                message: 'Update Failed',
                description: err?.response?.data?.message || 'Failed to update ticket type. Please try again.',
            });
            setState(state => ({ ...state, update_loading: false }));
        }
    };

    const deleteParkApplicableTicketType = async (park_applicable_ticket_type_id) => {
        try {
            await parkService?.deleteParkApplicableTicketType(park_applicable_ticket_type_id);
            notification?.success({
                message: 'Ticket Type Deleted',
                description: 'Ticket type has been removed and sequences updated automatically.',
            });
            refreshPage && refreshPage();
        } catch (err) {
            console.error('Delete failed:', err);
                notification.error({
                    message: 'Delete Failed',
                    description: err?.response?.data?.message || 'Failed to delete ticket type. Please try again.',
                });
        }
    };

    const fetchTicketTypes = async () => {
        try {
            setState(state => ({ ...state, loading: true }));
            const fetchTicketTypeResponse = await parkService?.getTicketType();
            const { ticket_types: all_ticket_types } = fetchTicketTypeResponse;

            setState(state => ({ ...state, loading: false, all_ticket_types: all_ticket_types }));
        } catch (err) {
            console.error('Fetch failed:', err);
            setState(state => ({ ...state, loading: false }));
        }
    };

    useEffect(() => {
        fetchTicketTypes();
    }, []);

    return (
        <>
            <DndProvider backend={HTML5Backend}>
                <div style={{ textAlign: 'start' }}>
                    <Row gutter={[12, 12]}>
                        {state.items.map((ticket_type, index) => (
                            <DragDropTicketTypeCard
                                key={ticket_type.ParkApplicableTicketType.park_applicable_ticket_type_id}
                                index={index}
                                ticket_type={ticket_type}
                                moveCard={moveCard}
                                handleDrop={handleDrop}
                                openEditModal={openEditModal}
                                deleteParkApplicableTicketType={deleteParkApplicableTicketType}
                                isUpdating={state.is_updating_sequence}
                            />
                        ))}

                        <Col span={4} xs={24} sm={8} md={6} xl={4}>
                            <Card
                                style={{
                                    background: 'var(--secondary-button-color)',
                                    height: '100%',
                                    cursor: 'pointer',
                                    border: '1px dashed var(--secondary-text-color)',
                                }}
                                styles={{
                                    body: {
                                        minHeight: 200,
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    },
                                }}
                                onClick={() => setState(state => ({ ...state, add_new_modal: true }))}
                            >
                                <div style={{ textAlign: 'center', color: 'var(--secondary-text-color)' }}>
                                    <div>
                                        <PlusOutlined />
                                    </div>
                                    <div>
                                        <span style={{ fontSize: 12, fontWeight: 500 }}>Add New Ticket Type</span>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </DndProvider>

            <Modal
                title="Add New Ticket Type"
                open={state?.add_new_modal}
                onCancel={() => {
                    setState(state => ({ ...state, add_new_modal: false }));
                    add_form?.resetFields();
                }}
                onOk={() => add_form.submit()}
                width={800}
                style={{ top: 20 }}
                confirmLoading={state?.save_loading}
                destroyOnClose
            >
                <Form form={add_form} layout='vertical' onFinish={saveParkApplicableTicketType}>
                    <AddTicketTypeModal form={add_form} selected_ticket_types={ticket_types} all_ticket_types={state?.all_ticket_types} />
                </Form>
            </Modal>

            <Modal
                title="Edit Ticket Type"
                open={state?.edit_modal}
                onCancel={() => {
                    setState(state => ({ ...state, edit_modal: null }));
                }}
                onOk={() => edit_form?.submit()}
                width={800}
                style={{ top: 20 }}
                confirmLoading={state?.update_loading}
                destroyOnClose
            >
                <Form form={edit_form} layout='vertical' onFinish={updateParkApplicableTicketType}>
                    <EditTicketTypeModal
                        park_applicable_ticket_type_id={state?.edit_modal}
                        form={edit_form}
                        selected_ticket_types={ticket_types}
                        all_ticket_types={state?.all_ticket_types}
                    />
                </Form>
            </Modal>
        </>
    );
};

export default TicketType;
